import { Dimensions, LoadsCube, Position } from 'src/app/core/types';

type DrawCube = Pick<LoadsCube, 'loadsPerAxis' | 'position' | 'dimensions'>;

interface Aabb {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

const FACE_TOUCH_EPS = 1e-6;

/**
 * Axis replication for drawing one load cube.
 * Keep loadsPerAxis for legitimate stacks (e.g. 6 columns × 5 high).
 * Collapse to 1×1×1 when expanding this cube would intersect another cube's
 * expanded volume (API already expanded, or duplicate positions).
 * Face-touching neighbours (columns side by side) do not count as overlap.
 */
export function loadsPerAxisForDrawing(
  loadCube: DrawCube,
  siblings: DrawCube[] = [],
  fallbackDimensions?: Dimensions,
): Position {
  const axes = loadCube.loadsPerAxis;
  if (!axes || (axes.x <= 1 && axes.y <= 1 && (axes.z ?? 1) <= 1)) {
    return axes ?? { x: 1, y: 1, z: 1 };
  }

  if (loadCube.position == null) {
    return axes;
  }

  const myBounds = expandedBounds(loadCube, fallbackDimensions);
  if (!myBounds) {
    return axes;
  }

  for (const other of siblings) {
    if (other === loadCube || other.position == null) {
      continue;
    }
    const otherBounds = expandedBounds(other, fallbackDimensions);
    if (otherBounds && aabbInteriorIntersect(myBounds, otherBounds)) {
      return { x: 1, y: 1, z: 1 };
    }
  }

  return axes;
}

function expandedBounds(cube: DrawCube, fallbackDimensions?: Dimensions): Aabb | null {
  const dims = cube.dimensions ?? fallbackDimensions;
  if (!dims || cube.position == null) {
    return null;
  }

  const width = dims.width ?? 0;
  const height = dims.height ?? 0;
  const length = dims.length ?? 0;
  const axes = cube.loadsPerAxis ?? { x: 1, y: 1, z: 1 };
  const z = cube.position.z ?? 0;

  return {
    minX: cube.position.x,
    maxX: cube.position.x + width * axes.x,
    minY: cube.position.y,
    maxY: cube.position.y + height * axes.y,
    minZ: z,
    maxZ: z + length * (axes.z ?? 1),
  };
}

/** True when boxes share interior volume; face contact alone is false. */
function aabbInteriorIntersect(a: Aabb, b: Aabb): boolean {
  return (
    rangesOverlapInterior(a.minX, a.maxX, b.minX, b.maxX) &&
    rangesOverlapInterior(a.minY, a.maxY, b.minY, b.maxY) &&
    rangesOverlapInterior(a.minZ, a.maxZ, b.minZ, b.maxZ)
  );
}

function rangesOverlapInterior(aMin: number, aMax: number, bMin: number, bMax: number): boolean {
  return aMin < bMax - FACE_TOUCH_EPS && aMax > bMin + FACE_TOUCH_EPS;
}
